import { Injectable } from '@angular/core';
import { AppConfigSettings, User, UserTenant } from '@arc/arc-shared-frontend';
import {
  GetSessionSummaryResponse,
  SessionPayment,
  PaymentRulesResponseV1,
} from '@arc/unified-payments-frontend/shared-payment';
import {
  datadogRum,
  DefaultPrivacyLevel,
  RumErrorEvent,
  RumResourceEvent,
  RumViewEvent,
} from '@datadog/browser-rum';

@Injectable({
  providedIn: 'root',
})
// https://docs.datadoghq.com/real_user_monitoring/
export class DataDogService {
  private defaultPrivacyLevel: DefaultPrivacyLevel =
    DefaultPrivacyLevel.MASK_USER_INPUT;

  private applicationId: string;
  private clientToken: string;
  private environment: string;
  private site: string;
  private service: string;
  private version: string;
  private initialized = false;
  private sessionReplayEnabled = false;
  private sessionReplaySampleRate = 0;
  private redactedUrlParameters: string[];

  /**
   * RUM error messages that match any of these patterns are dropped in
   * `beforeSend` and never submitted to Datadog. Use this list to suppress
   * known third-party / benign noise so real errors stay visible.
   *
   * Add a new entry with a short comment explaining why it is safe to drop.
   * Patterns must not use the `/g` flag — stateful `lastIndex` breaks
   * repeated `RegExp.test()` calls and causes intermittent missed matches.
   */
  private readonly ignoredErrorPatterns: readonly RegExp[] = [
    // Pendo analytics agent logs this via console.error when it cannot attach
    // click-event metadata. Handled internally by Pendo, no user impact.
    /Failed to add click event property/,
  ];

  constructor(private appConfigSetting: AppConfigSettings) {
    this.applicationId = this.appConfigSetting.getconfig(
      'dataDogApplicationId',
    );
    this.clientToken = this.appConfigSetting.getconfig('dataDogClientToken');
    this.site = this.appConfigSetting.getconfig('dataDogSite');
    this.service = this.appConfigSetting.getconfig('domainName');
    this.environment = this.appConfigSetting.getconfig('environment');
    this.version = this.appConfigSetting.getconfig('releaseTag');
    this.sessionReplayEnabled =
      this.appConfigSetting.getconfig('dataDogSessionReplayEnabled') === 'true';
    this.sessionReplaySampleRate = +this.appConfigSetting.getconfig(
      'dataDogSessionReplaySampleRate',
    );
    this.redactedUrlParameters = this.appConfigSetting
      .getconfig('redactedQueryParams')
      .split(',');
  }

  initializeRum(): void {
    try {
      datadogRum.init({
        applicationId: this.applicationId,
        clientToken: this.clientToken,
        site: this.site,
        service: this.service,
        env: this.environment,
        version: this.version,
        sessionSampleRate: 100,
        sessionReplaySampleRate: this.sessionReplaySampleRate, // Default: 20, 0 Disables Session Replays
        workerUrl: '/assets/datadog/worker.js',
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: this.defaultPrivacyLevel,
        beforeSend: (event) => {
          this.redactEventUrl(event);

          if (this.shouldIgnoreError(event)) {
            return false;
          }

          return true;
        },
      });
      this.initialized = true;
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error('Failed to initialize DataDog', error);
    }

    if (this.sessionReplayEnabled) {
      datadogRum.startSessionReplayRecording();
    }
  }

  public recordUser(user: User): void {
    datadogRum.setUserProperty('id', user.userId);
    datadogRum.setUserProperty('role', user.role);
    datadogRum.setUserProperty('teamName', 'checkout');
  }

  public recordUserTenant(user: UserTenant): void {
    datadogRum.setUserProperty('tenantId', user.tenantId);
    datadogRum.setUserProperty('tenantName', user.tenantName);
    datadogRum.setUserProperty('tenantType', user.tenantType);
  }

  public recordUserSession(session: GetSessionSummaryResponse): void {
    datadogRum.setUserProperty('pesession', {
      sessionid: session.sessionId,
      sessiontype: session.sessionType,
      customerId: session.customerId,
      buyerAccount: session.buyerAccountIdentifier,
      originApplication: session.originatingApplication,
    });
  }

  public recordUserPaymentRules(paymentRules: PaymentRulesResponseV1): void {
    const {
      payments,
      surcharge,
      generalPaymentSettings,
      shortAndOverPaySettings,
      contactInfoSettings,
      paymentLinksSettings,
      timedCardAcceptanceSettings,
    } = paymentRules.settings;

    datadogRum.setUserProperty('paymentRules', {
      requirecvv: payments.requireCVV,
      acceptBank: payments.acceptBank,
      acceptcreditcard: payments.acceptCreditCard,
      allowablecardbrands: payments.allowableCardBrands,
      acceptdirectdebit: payments.acceptDirectDebit,
      allowsurcharging: surcharge.allowSurcharging,
      restrictbycardtype: surcharge.restrictByCardType,
      restrictbyzipcode: surcharge.restrictByZipCode,
      zerodollarpaymentsenabled:
        generalPaymentSettings.zeroDollarPaymentsEnabled,
      shortpaynotificationemails:
        shortAndOverPaySettings.shortPayNotificationEmails,
      allowdisputereason: shortAndOverPaySettings.allowDisputeReason,
      allowfreeforreason: shortAndOverPaySettings.allowFreeFormReason,
      allowshortpay: shortAndOverPaySettings.allowShortPay,
      includecontactinfo:
        contactInfoSettings?.contactInfoMessage ||
        paymentLinksSettings?.contactInfoMessage,
      timedCardAcceptanceType:
        timedCardAcceptanceSettings?.timedCardAcceptanceType,
      timedCardAcceptanceDays:
        timedCardAcceptanceSettings?.timedCardAcceptanceNumberOfDays,
      timedCardAcceptanceEnabled: timedCardAcceptanceSettings?.enabled,
    });
  }

  public recordUserPayment(payment: SessionPayment): void {
    let isShortpay = false;
    let isOverpay = false;

    payment.transactions[0].payables.forEach((payable) => {
      if (payable.isOverPay) {
        isOverpay = true;
      }
      if (payable.isShortPay) {
        isShortpay = true;
      }
    });
    datadogRum.setUserProperty('payment', {
      method: payment.transactions[0].paymentAccount.paymentMethod ?? '',
      surcharge: payment.transactions[0].totalSurcharge !== 0,
      discount: payment.transactions[0].totalDiscount !== 0,
      shortpay: isShortpay,
      overpay: isOverpay,
      scheduled: payment.transactions[0].scheduled,
    });
  }

  logError(error: unknown, context?: Record<string, unknown>): void {
    if (!this.initialized) {
      return;
    }

    datadogRum.addError(error, context);
  }

  private redactUrlParameters(url: string): string {
    if (url == null) {
      return null;
    }

    let newUrl = url;

    for (const parameter of this.redactedUrlParameters) {
      newUrl = newUrl.replace(
        new RegExp(`${parameter}=[^&]*`),
        `${parameter}=REDACTED`,
      );
    }

    return newUrl;
  }

  private redactEventUrl(event: unknown) {
    if (this.isRumResourceEvent(event)) {
      event.resource.url = this.redactUrlParameters(event.resource.url);
      event.view.url = this.redactUrlParameters(event.view.url);
      event.view.referrer = this.redactUrlParameters(event.view.referrer);
      return;
    }

    if (this.isRumViewEvent(event)) {
      event.view.url = this.redactUrlParameters(event.view.url);
      event.view.referrer = this.redactUrlParameters(event.view.referrer);
      return;
    }

    if (this.isRumErrorEvent(event)) {
      event.error.resource.url = this.redactUrlParameters(
        event.error.resource.url,
      );
      return;
    }
  }

  private isRumResourceEvent(event: unknown): event is RumResourceEvent {
    const resourceEvent = event as RumResourceEvent;
    return resourceEvent.resource != null && resourceEvent.resource.url != null;
  }

  private isRumViewEvent(event: unknown): event is RumViewEvent {
    const resourceEvent = event as RumViewEvent;
    return resourceEvent.view != null;
  }

  private isRumErrorEvent(event: unknown): event is RumErrorEvent {
    const resourceEvent = event as RumErrorEvent;
    return resourceEvent.error?.resource != null;
  }

  private shouldIgnoreError(event: unknown): boolean {
    const errorEvent = event as RumErrorEvent;
    if (errorEvent?.type !== 'error') {
      return false;
    }

    const message = errorEvent.error?.message;
    if (!message) {
      return false;
    }

    return this.ignoredErrorPatterns.some((pattern) => pattern.test(message));
  }
}
